/**
 * Container style utilities for V2 grid cell layout.
 *
 * Grid cells use absolute positioning for image and text containers:
 * - imageContainer: behind text (z-index: 0)
 * - textContainer: above image (z-index: 1)
 * - checkboxContainer: above everything (z-index: 2)
 *
 * Each container reads settings from OptionsListStyle.appearance:
 * - imageContainer/textContainer: { heightKind, heightValue, verticalAlignment, insets }
 * - checkboxContainer: { position, margin }
 */

import type { CSSProperties } from 'react';

export interface ContainerSettings {
  heightKind?: string;
  heightValue?: number;
  verticalAlignment?: string;
  contentAlignment?: string;
  insets?: { top?: number; end?: number; bottom?: number; start?: number };
}

export interface ContainerDefaults {
  height: number;        // percent value (e.g. 75 means 75%)
  vAlign: string;        // 'top' | 'center' | 'bottom'
  contentAlign?: string; // default content alignment within container ('top' | 'center' | 'bottom')
}

export interface CheckboxContainerSettings {
  enabled?: boolean;
  position?: string;
  margin?: { top?: number; end?: number; bottom?: number; start?: number };
}

/**
 * Compute CSS for an absolute container within a grid cell.
 *
 * Uses percent-only height for now (heightKind is ignored, always treated as percent).
 * When container settings are null/undefined, falls back to variant-specific defaults.
 */
export function computeContainerStyle(
  container: ContainerSettings | undefined,
  defaults: ContainerDefaults,
): CSSProperties {
  const heightValue = container?.heightValue ?? defaults.height;
  const vAlign = container?.verticalAlignment ?? defaults.vAlign;
  const height = `${heightValue}%`;

  const style: CSSProperties = {
    position: 'absolute',
    left: 0,
    right: 0,
    height,
    overflow: 'hidden',
  };

  switch (vAlign) {
    case 'top':
      style.top = 0;
      break;
    case 'bottom':
      style.bottom = 0;
      break;
    case 'center':
      style.top = '50%';
      style.transform = 'translateY(-50%)';
      break;
  }

  // Apply content alignment (vertical alignment of content within container)
  const contentAlign = container?.contentAlignment ?? defaults.contentAlign;
  if (contentAlign) {
    style.display = 'flex';
    style.flexDirection = 'column';
    switch (contentAlign) {
      case 'top':
        style.justifyContent = 'flex-start';
        break;
      case 'center':
        style.justifyContent = 'center';
        style.alignItems = 'center';
        break;
      case 'bottom':
        style.justifyContent = 'flex-end';
        break;
    }
  }

  // Apply insets as padding
  if (container?.insets) {
    const ins = container.insets;
    if (ins.top != null) style.paddingTop = ins.top;
    if (ins.end != null) style.paddingRight = ins.end;
    if (ins.bottom != null) style.paddingBottom = ins.bottom;
    if (ins.start != null) style.paddingLeft = ins.start;
  }

  return style;
}

/**
 * Compute CSS for checkbox container overlay.
 *
 * Position values use RTL-aware naming:
 * - topStart/topEnd → top + insetInlineStart/insetInlineEnd
 * - bottomStart/bottomEnd → bottom + insetInlineStart/insetInlineEnd
 *
 * Margin values offset the container from the cell edge.
 */
export function computeCheckboxContainerStyle(
  container: CheckboxContainerSettings | undefined,
): CSSProperties | null {
  if (!container || !container.enabled) return null;

  const pos = container.position ?? 'topEnd';
  const m = container.margin;

  const style: CSSProperties = {
    position: 'absolute',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (pos.startsWith('top')) {
    style.top = m?.top ?? 4;
  } else {
    style.bottom = m?.bottom ?? 4;
  }

  if (pos.endsWith('Start')) {
    style.insetInlineStart = m?.start ?? 4;
  } else {
    style.insetInlineEnd = m?.end ?? 4;
  }

  return style;
}
